<template>
    <div class="v-select" :id="id" :class="{'v-select--expanded': isExpanded, 'v-select--disabled': disabled || options.length === 0 }" tabindex="0" @focus="focus" @blur="blur" @click="focus">
        <template v-if="isExpanded">
            <div class="v-select__scrollwrap" :style="style">
                <template v-if="ifScrollMustBeEnabled">
                    <vue-scroll :ops="ops" ref="vScroll">
                        <div class="v-select__scroll">
                            <table class="v-select__table">
                                <tr v-if="placeholder !== ''" class="v-select__row v-select__row--placeholder v-select__row--disabled">
                                    <td class="v-select__option">
                                        <span class="v-select__option__text" v-text="placeholder"></span>
                                    </td>
                                </tr>
                                <tr v-for="option in options" :key="option[trackBy]" @mousedown="change(option)" class="v-select__row" :class="{'v-select__row--selected': isSelected(option), 'v-select__row--disabled': isDisabled(option)}">
                                    <td class="v-select__option" :class="{'v-select__option--wrapped': ifWrapMustBeEnabled, 'v-select__option--selected': isSelected(option)}">
                                        <span class="v-select__option__text" v-text="option[labelBy]"></span>
                                    </td>
                                </tr>
                            </table>

                            <div class="v-select__vruler" ref="vRuler"></div>
                        </div>
                    </vue-scroll>
                </template>
                <template v-else>
                    <div class="v-select__scroll">
                        <table class="v-select__table">
                            <tr v-if="placeholder !== ''" class="v-select__row v-select__row--placeholder v-select__row--disabled">
                                <td v-text="placeholder" class="v-select__option"></td>
                            </tr>
                            <tr v-for="option in options" :key="option[trackBy]" @mousedown="change(option)" class="v-select__row" :class="{'v-select__row--selected': isSelected(option), 'v-select__row--disabled': isDisabled(option)}">
                                <td class="v-select__option" :class="{'v-select__option--wrapped': ifWrapMustBeEnabled, 'v-select__option--selected': isSelected(option)}">
                                    <span class="v-select__option__text" v-text="option[labelBy]"></span>
                                </td>
                            </tr>
                        </table>

                        <div class="v-select__vruler" ref="vRuler"></div>
                    </div>
                </template>
            </div>
        </template>

        <div v-else class="v-select__view" :title="selectedLabel">
            <template v-if="selected !== null">
                   <span class="v-select__view__wrap">
                       <span class="v-select__view__text">
                           <span class="v-select__view__prefix" v-if="prefix !== ''" v-text="`${prefix} `"></span>
                           <span v-text="selectedLabel"></span>
                       </span>
                   </span>
            </template>
            <span v-else-if="placeholder !== ''" class="v-select__placeholder" v-text="placeholder"></span>

            <div class="v-select__view__icon">
                <slot name="icon">
<!-- Loader                   <span class="u-fa u-fa-spinner u-fa-pulse u-fa-3x u-fa-fw"></span>-->
                    <div class="v-select__view__icon-default"></div>
                </slot>
            </div>
        </div>

        <div class="v-select__hruler" ref="hRuler">
            <span v-text="longestLabel"></span>
        </div>
    </div>
</template>
<script lang="ts">
import { mobile, tablet } from 'bowser';
import get from 'lodash/get';
import { Component, Prop, Watch } from 'vue-property-decorator';
import BaseComponent from './Base';

@Component({
      inheritAttrs: false,
    })
    export default class TableSelect extends BaseComponent {
        @Prop({ type: String, default: null }) protected readonly id: string;
        @Prop({ default: null }) protected readonly value: any;
        @Prop({ type: String, default: '' }) protected readonly placeholder: string;
        @Prop({ type: String, default: '' }) protected readonly prefix: string;
        @Prop({ type: Boolean, default: false }) protected readonly disabled: boolean;
        @Prop({ type: String, default: 'id' }) protected readonly trackBy: string;
        @Prop({ type: String, default: 'name' }) protected readonly labelBy: string;
        @Prop({ type: Boolean, default: false }) protected readonly closeWhenEmpty: boolean;
        @Prop({ type: Number, default: 200 }) protected readonly maxHeight: number;
        @Prop({ type: Array, default: () => [] }) protected readonly options: any[];
        @Prop({ type: Function, default: () => () => false }) protected readonly disabledOptionCallback: (option: any, instance: any) => any;

        protected isMounted: boolean = false;
        protected innerIsExpanded: boolean = false;
        protected selectHeight: number = 0;
        protected windowWidth: number = 0;
        protected maxRulerWidth: number = 0;
        protected windowResizeTimer: any = null;
        protected elementResizeTimer: any = null;

        protected get ops(): any {
            const config = {
                vuescroll: {
                    mode: 'native',
                },
                scrollPanel: {
                    scrollingX: false,
                    speed: 10,
                    easing: 'easeInCubic',
                },
                rail: {},
                bar: {
                    background: '#00a650',
                    keepShow: true,
                    minSize: 0.3,
                },
            };

            if (mobile || tablet) {
                config.rail = {
                    background: 'rgba(0,0,0, .07)',
                    width: '8px',
                    opacity: 1,
                };
                config.bar = {
                    background: 'rgba(0,0,0, .3)',
                    keepShow: true,
                    minSize: 0.3,
                };
            }

            return config;
        }

        protected get isExpanded(): boolean {
            return this.innerIsExpanded;
        }

        protected set isExpanded(state: boolean) {
            this.innerIsExpanded = state === true;

            if (state === true) {
                this.$nextTick(() => {
                    this.updateWindowWidth();
                    this.updateMaxRulerWidth();
                    this.updateSelectHeight();
                    this.focusSelectedOption();
                });
            }
        }

        protected get longestLabel(): string {
            if (this.options.length === 0) {
                return '';
            }
            return this.options.map((o) => o[this.labelBy]).reduce((a, b) => {
                if (!Array.isArray(a) || !Array.isArray(b)) {
                    return a;
                }
                return a.length > b.length ? a : b;
            });
        }

        protected get style(): { width?: string, height?: string } {
            if (!this.isExpanded) {
                return {};
            }
            const width = this.maxWidth > this.selectWidth ? this.maxWidth + 'px' : 'auto';
            if (this.selectHeight !== 0) {
                const height = this.selectHeight < this.maxHeight ? this.selectHeight + 12 + 'px' : this.maxHeight + 'px';
                return { width, height };
            }
            return { width };
        }

        /**
         * TODO: test it as the expression may be incorrect
         */
        protected get ifScrollMustBeEnabled(): boolean {
            return this.selectHeight === 0 || this.selectHeight > this.maxHeight;
        }

        protected get ifWrapMustBeEnabled(): boolean {
            return this.maxWidth >= this.rightWindowSideDistance;
        }

        protected get selectWidth(): number {
            if (!this.isMounted || !this.isExpanded) {
                return 0;
            }
            return this.$el.offsetWidth;
        }

        protected get maxWidth(): number {
            return this.maxRulerWidth < this.rightWindowSideDistance ? this.maxRulerWidth : this.rightWindowSideDistance;
        }

        protected get rightWindowSideDistance(): number {
            const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
            const leftOffset = this.$el.getBoundingClientRect().left + scrollLeft;
            // 3 is for not getting too close to the window edge
            return this.windowWidth - leftOffset - 3;
        }

        protected get selected(): any {
            if (this.selectedId === null) {
                return null;
            }
            return this.options.find((option) => option[this.trackBy] === this.selectedId) || null;
        }

        protected get selectedId(): any {
            return get(this.value, this.trackBy, null);
        }

        protected get selectedLabel(): any {
            return this.selected !== null ? this.selected[this.labelBy] : null;
        }

        /**
         * Trying to focus first selected option when the select being expanded.
         */
        public focusSelectedOption(): void {
            const options = this.$el.getElementsByClassName('v-select__option--selected');
            if (options.length === 0 || typeof this.$refs.vScroll === 'undefined') {
                return;
            }
            (this.$refs.vScroll as any).scrollIntoView(options[0]);
        }

        public isSelected(option: any): boolean {
            if (this.selectedId === null) {
                return false;
            }
            return get(option, this.trackBy) === this.selectedId;
        }

        public isDisabled(option): boolean {
            return this.disabledOptionCallback(option, this) === true;
        }

        public getValue(): any {
            return this.value;
        }

        public focus(): void {
            if (/*this.options.length > 0 && */this.isExpanded === false) {
                this.isExpanded = true;
                this.$emit('expand', true, this.id);
            }
        }

        public blur(): void {
            if (this.isExpanded === true && this.selected !== null || this.closeWhenEmpty) {
                this.isExpanded = false;
                this.$emit('expand', false, this.id);
            }
        }

        public change(option: any): void {
            if (this.isDisabled(option)) {
                return;
            }
            this.$emit('input', option, this.id);
            this.$el.blur();
        }

        public expand(state?: boolean): void {
            if (state !== false) {
                this.focus();
            } else {
                this.blur();
            }
        }

        public updateSelectHeight(): void {
            if (this.$refs.vRuler instanceof Element) {
                this.selectHeight = this.$refs.vRuler.getBoundingClientRect().height;
            }
        }

        public updateMaxRulerWidth(): void {
            if (this.$refs.hRuler instanceof Element) {
                this.maxRulerWidth = this.$refs.hRuler.getBoundingClientRect().width;
            }
        }

        public updateWindowWidth(): void {
            this.windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        }

        public onWindowResizeEnd(): void {
            if (this.isExpanded) {
                this.isExpanded = false;
                // $nextTick is required here
                this.$nextTick(() => {
                    this.isExpanded = true;
                });
            }
        }

        public onWindowResize(): void {
            clearTimeout(this.windowResizeTimer);
            this.windowResizeTimer = setTimeout(this.onWindowResizeEnd, 750);
        }

        /**
         * This will not work if the element has a position:fixed rule.
         */
        public isElementVisible(): boolean {
            return this.$el.offsetParent !== null;
        }

        /**
         * At the moment, the form is not displayed when the component is initialized.
         * Because of this, we need to use a ticker to change the size of the active
         * control when it becomes visible.
         * When the A/B tests are completed, you can delete these two methods and a ticker.
         */
        public startResizeTicker(): void {
            clearInterval(this.elementResizeTimer);
            this.elementResizeTimer = setInterval(this.stopResizeTicker, 500);
        }

        public stopResizeTicker(): void {
            if (!this.isExpanded) {
                return;
            }

            if (this.isElementVisible()) {
                clearInterval(this.elementResizeTimer);
                this.onWindowResizeEnd();
            }
        }

        protected mounted(): void {
            /**
             * This tricks vue to update computed properties when the component is mounted.
             */
            this.isMounted = true;

            window.addEventListener('resize', this.onWindowResize, { passive: true });

            if (!this.isElementVisible()) {
                this.startResizeTicker();
            }
        }

        protected beforeDestroy(): void {
            window.removeEventListener('resize', this.onWindowResize);
        }

        @Watch('options')
        protected onOptionsChanged(): void {
            if (this.isExpanded !== true) {
                return;
            }

            this.$nextTick(() => {
                this.updateWindowWidth();
                this.updateMaxRulerWidth();
                this.updateSelectHeight();
            });
        }
    }
</script>
